import { doc, getDoc, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/config";
import { generateDateID } from "./dayPostService";
import { callAi } from "./aiClient";

interface InsightCache {
  insight: string;
  timestamp: number;
  dataType: string;
  timeframe: string;
}

interface StoredInsight {
  insight: string;
  dataType: string;
  timeframe: string;
  dateId: string;
  createdAt: number;
  userId: string;
}

// Response shape from the buildDailyInsightPrompt callable.
type BuildPromptResponse =
  | { available: false }
  | { available: true; systemInstruction: string; prompt: string };

// In-memory cache for insights
const insightCache: Map<string, InsightCache> = new Map();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour cache

/**
 * Check if running in staging mode
 */
function isStaging(): boolean {
  const isProduction =
    process.env.REACT_APP_FORCE_PRODUCT_MODE === "true" ||
    (typeof window !== "undefined" &&
      (window.location.hostname === "peakxv2.web.app" ||
        window.location.hostname === "peak.app" ||
        window.location.hostname === "www.peak.app"));
  return !isProduction;
}

/**
 * Generate cache key for insights
 */
function getCacheKey(
  userId: string,
  dataType: string,
  timeframe: string,
): string {
  const today = generateDateID(new Date());
  return `${userId}_${dataType}_${timeframe}_${today}`;
}

/**
 * Get cached insight if still valid
 */
export function getCachedInsight(
  userId: string,
  dataType: string,
  timeframe: string,
): string | null {
  const key = getCacheKey(userId, dataType, timeframe);
  const cached = insightCache.get(key);

  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log("🎯 Using cached insight");
    return cached.insight;
  }

  return null;
}

/**
 * Cache insight in memory
 */
export function cacheInsight(
  userId: string,
  dataType: string,
  timeframe: string,
  insight: string,
): void {
  const key = getCacheKey(userId, dataType, timeframe);
  insightCache.set(key, {
    insight,
    timestamp: Date.now(),
    dataType,
    timeframe,
  });
}

/**
 * Get stored insight from Firestore
 */
async function getStoredInsight(
  userId: string,
  dataType: string,
  timeframe: string,
  dateId: string,
): Promise<string | null> {
  try {
    const insightRef = doc(
      db,
      "aiInsights",
      userId,
      "insights",
      `${dateId}_${dataType}_${timeframe}`,
    );

    const insightDoc = await getDoc(insightRef);

    if (insightDoc.exists()) {
      const data = insightDoc.data() as StoredInsight;
      console.log(
        `🎯 Found stored insight for ${dateId} ${dataType} ${timeframe}`,
      );
      return data.insight;
    }

    return null;
  } catch (error) {
    console.error("Error fetching stored insight:", error);
    return null;
  }
}

/**
 * Save insight to Firestore
 */
async function saveInsightToFirestore(
  userId: string,
  dataType: string,
  timeframe: string,
  dateId: string,
  insight: string,
): Promise<void> {
  try {
    const insightRef = doc(
      db,
      "aiInsights",
      userId,
      "insights",
      `${dateId}_${dataType}_${timeframe}`,
    );

    const insightData: StoredInsight = {
      insight,
      dataType,
      timeframe,
      dateId,
      createdAt: Date.now(),
      userId,
    };

    await setDoc(insightRef, insightData);
    console.log(
      `✅ Saved insight to Firestore for ${dateId} ${dataType} ${timeframe}`,
    );
  } catch (error) {
    console.error("Error saving insight to Firestore:", error);
    // Don't throw - we can still use the insight even if we can't save it
  }
}

/**
 * Main function to generate personalized insights.
 *
 * The athlete report + prompt + system instruction are built server-side by the
 * `buildDailyInsightPrompt` Firebase Function so the logic lives in a single
 * place across all client platforms. The model call goes through aiProxy,
 * which checks the athlete's monthly allowance and records the usage.
 */
export async function generateInsights(
  userId: string,
  dataType: string = "training",
  timeframe: "Day" | "Week" | "Month" = "Day",
  waitForConsistency: boolean = false,
  bypassCache: boolean = false,
): Promise<string | null> {
  try {
    console.log(
      `🎯 Generating ${timeframe} ${dataType} insight for user ${userId}`,
    );

    // Check if running in staging mode
    if (isStaging()) {
      console.log("⚠️ AI Insights disabled in staging environment");
      return "AI Insights are not configured to work in the Staging environment. To test insights, run: REACT_APP_FORCE_PRODUCT_MODE=true npm start";
    }

    // Get current date ID for today
    const currentDateId = generateDateID(new Date());

    // Skip cache checks if bypassCache is true
    if (!bypassCache) {
      // Check Firestore first for today's insight
      const storedInsight = await getStoredInsight(
        userId,
        dataType,
        timeframe,
        currentDateId,
      );
      if (storedInsight) {
        // Also cache it in memory for this session
        cacheInsight(userId, dataType, timeframe, storedInsight);
        return storedInsight;
      }

      // Check memory cache (in case it was generated earlier in this session)
      const cachedInsight = getCachedInsight(userId, dataType, timeframe);
      if (cachedInsight) {
        return cachedInsight;
      }
    } else {
      console.log("🔄 Bypassing cache - generating fresh insight");
    }

    // When called right after peak score calculation, wait briefly so the
    // server reads fresh data (Firestore consistency).
    if (waitForConsistency) {
      console.log(
        "⏱️ Waiting briefly for Firestore consistency before building prompt...",
      );
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }

    // Build the system instruction + prompt server-side (single source of truth).
    const buildDailyInsightPrompt = httpsCallable<
      { dataType: string; timeframe: string; dateID: string },
      BuildPromptResponse
    >(functions, "buildDailyInsightPrompt");

    const { data } = await buildDailyInsightPrompt({
      dataType,
      timeframe,
      dateID: currentDateId,
    });

    if (!data.available) {
      console.log("ℹ️ No peak score available for today — skipping insight");
      return null;
    }

    const { systemInstruction, prompt } = data;

    // Debug: log the full prompt and system instruction being sent to Gemini
    console.log("═══════════════════════════════════════════════════════");
    console.log("🧠 DEBUG: SYSTEM INSTRUCTION");
    console.log("═══════════════════════════════════════════════════════");
    console.log(systemInstruction);
    console.log("═══════════════════════════════════════════════════════");
    console.log("📝 DEBUG: FULL PROMPT SENT TO GEMINI");
    console.log("═══════════════════════════════════════════════════════");
    console.log(prompt);
    console.log("═══════════════════════════════════════════════════════");

    // Generate insight
    console.log("📤 Sending to Gemini for insight generation...");
    const result = await callAi({
      feature: "insights",
      model: "gemini-2.5-flash",
      contents: [{ role: "user", parts: [{ text: prompt }] }],
      systemInstruction,
      generationConfig: {
        maxOutputTokens: 1000,
        temperature: 0.7, // Balanced creativity — grounded in data but with natural, human-feeling language
        thinkingBudget: 0, // disable thinking tokens so full budget goes to output
      },
    });
    const insight = result.text;

    // Debug: log the raw response
    console.log("═══════════════════════════════════════════════════════");
    console.log("✅ DEBUG: RAW GEMINI RESPONSE");
    console.log("═══════════════════════════════════════════════════════");
    console.log(insight);
    console.log("═══════════════════════════════════════════════════════");

    // Validate, cache, and save the insight
    if (insight && insight.trim() !== "") {
      // Cache in memory for this session
      cacheInsight(userId, dataType, timeframe, insight);

      // Save to Firestore for persistence
      await saveInsightToFirestore(
        userId,
        dataType,
        timeframe,
        currentDateId,
        insight,
      );

      console.log("✅ Insight generated, cached, and saved successfully");
      return insight;
    } else {
      throw new Error("Empty insight received from AI");
    }
  } catch (error) {
    console.error("❌ Error generating AI insight:", error);
    return null;
  }
}
